// Belege per E-Mail senden (docs/anmeldung/SPEZIFIKATION.md §5.1–§5.4, §9.1, §10.5): gemeinsame Formen der
// Sendeaktionen der Fachmodule (finance, sales, purchasing) und des Senden-Dialogs (web/api/mail). Ein Ort, damit
// alle Belege gleich aussehen und der Dialog jede Sendeaktion bedienen kann.
//
//   <modul>.<beleg>.send         { documentId, to?, cc?, message? (+ eigene Felder) } → MailQueuedResult
//   <modul>.<beleg>.sendPreview  dieselbe Eingabe (nur lesen)                         → MailPreviewResult
import { z } from 'zod'
import { zId } from './ids'
import { MAIL_LIMITS, zMailAddress, zMailMessageText } from './mail'

const recipientsError = `Eine E-Mail geht an höchstens ${MAIL_LIMITS.recipients} Empfänger.`

/** Empfänger einer Sendeaktion: ohne Angabe gilt der Vorschlag aus den Stammdaten */
export const zMailRecipients = z.array(zMailAddress).max(MAIL_LIMITS.recipients, { error: recipientsError })

/** Grundform jeder Sendeaktion eines Belegs (Module erweitern sie, z. B. um includeEInvoice) */
export const documentMailInputShape = {
  documentId: zId,
  /** ohne Angabe: Vorschlag aus den Stammdaten (mail.partnerRecipients) */
  to: zMailRecipients.optional(),
  cc: zMailRecipients.optional(),
  /** persönliche Zeile der sendenden Person (Klartext, höchstens 1000 Zeichen) */
  message: zMailMessageText.nullish(),
} as const
export const zDocumentMailInput = z.strictObject(documentMailInputShape)
export type DocumentMailInput = z.input<typeof zDocumentMailInput>

/** Ergebnis einer Sendeaktion: die vorgemerkte Nachricht */
export const zMailQueuedResult = z.object({
  mailId: zId,
  /** queued: geht nach dem Rückgängig-Fenster hinaus · held: kein Versandweg eingerichtet · suppressed: alle gesperrt */
  status: z.enum(['queued', 'held', 'suppressed']),
  notBefore: z.date(),
  /** Rückgängig-Fenster in Sekunden (0 = sofort) */
  delaySeconds: z.int(),
  subject: z.string(),
  recipients: z.array(
    z.object({ address: z.string(), status: z.enum(['pending', 'suppressed']), reason: z.string().nullable() }),
  ),
  /** angehängte Dateien (E-Rechnung) mit Prüfsumme */
  attachments: z.array(z.object({ name: z.string(), kind: z.string(), sha256: z.string() })),
  /** fertiger Satz für den Hinweis nach dem Senden */
  message: z.string(),
})
export type MailQueuedResult = z.output<typeof zMailQueuedResult>

/** Vorschau im Senden-Dialog: Vorschlag, gerenderte Nachricht, Anhänge, Hinweise (nichts wird vorgemerkt) */
export const zMailPreviewResult = z.object({
  /** Beleg bzw. Mahnung */
  id: zId,
  /** Belegart in Worten („Rechnung“) und Nummer */
  title: z.string(),
  number: z.string().nullable(),
  /** Empfänger, für die gerendert wurde: die Eingabe bzw. der Vorschlag */
  to: z.array(z.object({ address: z.string(), name: z.string().nullable() })),
  cc: z.array(z.object({ address: z.string(), name: z.string().nullable() })),
  /** Vorschlag aus den Stammdaten (leer: keine Adresse hinterlegt) */
  suggestion: z.array(z.object({ address: z.string(), name: z.string().nullable() })),
  /** gesperrte Empfänger mit Grund */
  suppressed: z.array(z.object({ address: z.string(), reason: z.string() })),
  from: z.object({ name: z.string(), address: z.string() }),
  replyTo: z.string().nullable(),
  subject: z.string(),
  html: z.string(),
  text: z.string(),
  attachments: z.array(z.object({ name: z.string(), contentType: z.string(), sizeBytes: z.int(), kind: z.string() })),
  delaySeconds: z.int(),
  /** Satz zum Versandweg (kein Versandweg, Demo, Fangstelle) – sonst null */
  notice: z.string().nullable(),
  /** Satz, warum so nicht gesendet werden kann (keine Adresse hinterlegt …) – sonst null */
  problem: z.string().nullable(),
  /** Rechnungen: E-Rechnung liegt am Beleg und kann anhängen */
  eInvoice: z.object({ available: z.boolean(), name: z.string().nullable() }).nullable(),
  /** Rechnungen ohne E-Rechnung im Anhang: Bestätigung der Zustimmung des Kunden nötig (sonstige Rechnung, §5.1) */
  consentRequired: z.boolean(),
  /** schon per E-Mail versandt (vom Mailserver angenommen bzw. abgelegt) – Hinweis vor dem erneuten Senden */
  alreadySent: z.boolean(),
})
export type MailPreviewResult = z.output<typeof zMailPreviewResult>

/** Ein Empfänger im Ergebnis eines Sammelversands (Rechnungslauf, Mahnlauf) */
export const zMailBatchRow = z.object({
  /** Beleg bzw. Mahnung */
  id: zId,
  number: z.string().nullable(),
  partnerName: z.string(),
  /** Adresse aus den Stammdaten – null: zum Drucken */
  address: z.string().nullable(),
  /** warum nicht per E-Mail (keine Adresse, gesperrt, zurückgenommen …) */
  reason: z.string().nullable(),
  /** schon per E-Mail versandt */
  alreadySent: z.boolean(),
  /** Rechnung ohne E-Rechnung im Anhang: braucht die Zustimmung des Kunden */
  needsConsent: z.boolean(),
})
export type MailBatchRow = z.output<typeof zMailBatchRow>

export const zMailBatchTargets = z.object({
  rows: z.array(zMailBatchRow),
  /** gehen per E-Mail hinaus (Adresse vorhanden, noch nicht versandt) */
  withAddress: z.int(),
  /** bleiben zum Drucken (ohne Adresse oder nicht versendbar) */
  toPrint: z.int(),
  /** schon versandt – werden nicht noch einmal geschickt */
  alreadySent: z.int(),
  /** Rechnungen ohne E-Rechnung im Anhang: gehen nur mit bestätigter Zustimmung per E-Mail (sonst zum Drucken) */
  consentNeeded: z.int(),
})
export type MailBatchTargets = z.output<typeof zMailBatchTargets>

export const zMailBatchResult = z.object({
  sent: z.array(z.object({ id: zId, number: z.string().nullable(), mailId: zId, address: z.string() })),
  toPrint: z.array(z.object({ id: zId, number: z.string().nullable(), partnerName: z.string(), reason: z.string() })),
  skipped: z.int(),
  message: z.string(),
})
export type MailBatchResult = z.output<typeof zMailBatchResult>

export const MAIL_SEND_TEXTS = {
  noAddress: 'Für diesen Empfänger ist keine E-Mail-Adresse hinterlegt. Bitte geben Sie eine Adresse ein.',
  noAddressPrint: 'keine E-Mail-Adresse hinterlegt',
  consentRequired: 'Bitte bestätigen Sie, dass der Kunde mit Rechnungen per E-Mail einverstanden ist.',
  consentLabel: 'Der Kunde ist mit Rechnungen per E-Mail einverstanden.',
  consentHint: 'Ohne E-Rechnung gilt die E-Mail als „sonstige Rechnung“ und braucht die Zustimmung des Empfängers.',
  alreadySent: 'bereits per E-Mail versandt',
} as const

/** „in 20 Sekunden“ – Teil der Sätze nach dem Senden */
export const inSeconds = (seconds: number): string =>
  seconds <= 0 ? 'gleich' : seconds === 1 ? 'in einer Sekunde' : `in ${seconds} Sekunden`

/** Satz nach dem Vormerken: „Rechnung RE-… wird in 20 Sekunden an a@b.de versendet.“ */
export function mailQueuedMessage(input: {
  label: string
  status: 'queued' | 'held' | 'suppressed'
  delaySeconds: number
  addresses: readonly string[]
}): string {
  const to =
    input.addresses.length <= 2
      ? input.addresses.join(' und ')
      : `${input.addresses.slice(0, 2).join(', ')} und ${input.addresses.length - 2} weitere`
  if (input.status === 'suppressed') return `${input.label}: Alle Empfänger sind gesperrt – es wurde nichts versendet.`
  if (input.status === 'held')
    return `${input.label} an ${to} ist vorgemerkt. Auf diesem Server ist noch kein Versandweg eingerichtet – die E-Mail geht hinaus, sobald er steht.`
  return `${input.label} wird ${inSeconds(input.delaySeconds)} an ${to} versendet.`
}

// ---- Bausteine der Sendeaktionen (rein – Server der Module) ---------------------------------------------------------

/** „a@b.de, c@d.de und 2 weitere“ */
export const mailAddressesText = (list: readonly string[]): string =>
  list.length <= 3 ? list.join(', ') : `${list.slice(0, 3).join(', ')} und ${list.length - 3} weitere`

/** Ergebnis von mail.send() – Form wie MailQueued des Kerns */
export interface QueuedMailLike {
  mailId: string
  status: 'queued' | 'held' | 'suppressed'
  notBefore: Date
  recipients: { address: string; status: 'pending' | 'suppressed'; reason: string | null }[]
}

/** Ergebnis einer Sendeaktion aus der vorgemerkten Nachricht */
export function toMailQueuedResult(input: {
  label: string
  queued: QueuedMailLike
  subject: string
  attachments?: readonly { name: string; kind: string; sha256: string }[]
  now: Date
}): MailQueuedResult {
  const { queued } = input
  const delaySeconds = Math.max(Math.round((queued.notBefore.getTime() - input.now.getTime()) / 1000), 0)
  const pending = queued.recipients
    .filter((recipient) => recipient.status === 'pending')
    .map((recipient) => recipient.address)
  return {
    mailId: queued.mailId,
    status: queued.status,
    notBefore: queued.notBefore,
    delaySeconds,
    subject: input.subject,
    recipients: queued.recipients,
    attachments: (input.attachments ?? []).map((attachment) => ({
      name: attachment.name,
      kind: attachment.kind,
      sha256: attachment.sha256,
    })),
    message: mailQueuedMessage({
      label: input.label,
      status: queued.status,
      delaySeconds,
      addresses: pending.length > 0 ? pending : queued.recipients.map((recipient) => recipient.address),
    }),
  }
}

/** „Rückgängig“ im Fenster vor dem Versand: die vorgemerkte Nachricht abbrechen (platform.mail.cancel) */
export const mailCancelOffer = (
  output: MailQueuedResult,
): { action: string; input: { mailId: string }; label: string } | undefined =>
  output.status === 'suppressed'
    ? undefined
    : { action: 'platform.mail.cancel', input: { mailId: output.mailId }, label: 'E-Mail nicht senden' }

/** Vorschau-Karte (plan) einer Sendeaktion: Empfänger, Betreff, Anhänge, Zeitpunkt */
export function mailSendPreviewCard(
  output: MailQueuedResult,
  title: string,
): {
  title: string
  summary: string
  diff: { label: string; before: null; after: string }[]
  undo: { possible: boolean; label?: string }
} {
  const to = output.recipients
    .filter((recipient) => recipient.status === 'pending')
    .map((recipient) => recipient.address)
  const suppressed = output.recipients.filter((recipient) => recipient.status === 'suppressed')
  return {
    title,
    summary:
      suppressed.length > 0
        ? `${output.message} Gesperrt: ${suppressed.map((recipient) => `${recipient.address} (${recipient.reason ?? 'Sperrliste'})`).join(', ')}.`
        : output.message,
    diff: [
      { label: 'An', before: null, after: to.length > 0 ? mailAddressesText(to) : '–' },
      { label: 'Betreff', before: null, after: output.subject },
      ...(output.attachments.length > 0
        ? [{ label: 'Anhang', before: null, after: output.attachments.map((attachment) => attachment.name).join(', ') }]
        : []),
      {
        label: 'Versand',
        before: null,
        after:
          output.status === 'held'
            ? 'gesammelt – noch kein Versandweg eingerichtet'
            : output.delaySeconds > 0
              ? `in ${output.delaySeconds} Sekunden (bis dahin rückgängig)`
              : 'sofort',
      },
    ],
    // rückgängig nur im Fenster vor dem Versand (bzw. solange gesammelt wird)
    undo:
      output.status === 'held'
        ? { possible: true, label: 'bis zum Versand' }
        : output.status === 'queued' && output.delaySeconds > 0
          ? { possible: true, label: `in den nächsten ${output.delaySeconds} Sekunden` }
          : { possible: false },
  }
}

/** Ergebnis von mail.preview() – Form wie MailPreview des Kerns */
export interface RenderedMailLike {
  subject: string
  html: string
  text: string
  from: { name: string; address: string }
  replyTo: string | null
  recipients: { address: string; suppressed: string | null }[]
  attachments: { name: string; contentType: string; sizeBytes: number; kind: string }[]
  delaySeconds: number
  notice: string | null
}

type AddressLike = { address: string; name?: string | null }
const addressRows = (list: readonly AddressLike[]): { address: string; name: string | null }[] =>
  list.map((entry) => ({ address: entry.address, name: entry.name ?? null }))

/** Vorschau für den Senden-Dialog aus mail.preview() und den Angaben des Moduls */
export function toMailPreviewResult(input: {
  id: string
  title: string
  number: string | null
  to: readonly AddressLike[]
  cc: readonly AddressLike[]
  suggestion: readonly AddressLike[]
  preview: RenderedMailLike
  problem?: string | null
  eInvoice?: MailPreviewResult['eInvoice']
  consentRequired?: boolean
  alreadySent: boolean
}): MailPreviewResult {
  const { preview } = input
  return {
    id: input.id,
    title: input.title,
    number: input.number,
    to: addressRows(input.to),
    cc: addressRows(input.cc),
    suggestion: addressRows(input.suggestion),
    suppressed: preview.recipients
      .filter((recipient) => recipient.suppressed !== null)
      .map((recipient) => ({ address: recipient.address, reason: recipient.suppressed as string })),
    from: preview.from,
    replyTo: preview.replyTo,
    subject: preview.subject,
    html: preview.html,
    text: preview.text,
    attachments: preview.attachments.map((attachment) => ({
      name: attachment.name,
      contentType: attachment.contentType,
      sizeBytes: attachment.sizeBytes,
      kind: attachment.kind,
    })),
    delaySeconds: preview.delaySeconds,
    notice: preview.notice,
    problem: input.problem ?? (input.to.length === 0 ? MAIL_SEND_TEXTS.noAddress : null),
    eInvoice: input.eInvoice ?? null,
    consentRequired: input.consentRequired ?? false,
    alreadySent: input.alreadySent,
  }
}

/** Ergebnis eines Sammelversands als ein Satz */
export function mailBatchMessage(input: {
  sent: number
  toPrint: number
  skipped: number
  one: string
  many: string
}): string {
  const parts = [
    input.sent === 1 ? `1 ${input.one} geht per E-Mail hinaus` : `${input.sent} ${input.many} gehen per E-Mail hinaus`,
    input.toPrint > 0 ? `${input.toPrint} zum Drucken` : null,
    input.skipped > 0 ? `${input.skipped} schon versandt` : null,
  ].filter(Boolean)
  return `${parts.join(' · ')}.`
}

/** Zustände einer Nachricht, die noch hinausgeht oder schon angenommen ist („schon versandt“) */
export const MAIL_LIVE_STATUSES = ['queued', 'sending', 'retry', 'held', 'sent'] as const

/** Preis-Vorschau ohne das Recht „Einstandspreise sehen“ (Bestellungen) */
export const MAIL_PREVIEW_HIDDEN = {
  text: 'Die Vorschau enthält Einkaufspreise und ist nur mit dem Recht „Einstandspreise sehen“ sichtbar. Die E-Mail an den Lieferanten enthält sie.',
  html: '<!doctype html><html lang="de"><body style="font-family:system-ui,sans-serif;padding:24px;color:#3f3f46"><p>Die Vorschau enthält Einkaufspreise und ist nur mit dem Recht „Einstandspreise sehen“ sichtbar. Die E-Mail an den Lieferanten enthält sie.</p></body></html>',
} as const
